import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorChecklistDefaults, clientChecklistDefaults, calculateDueDate } from '../../data/defaultChecklists';

const CreateEventModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [billingModel, setBillingModel] = useState('Contract');
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: 'Wedding',
    eventDate: '',
    clientName: '',
    clientEmail: '',
    depositAmount: '',
    depositDue: '',
    message: '',
    contractFile: null
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [quoteItems, setQuoteItems] = useState([{ id: 1, item: '', units: 1, amount: '' }]);

  const updateQuoteItem = (id, field, value) => {
    setQuoteItems(quoteItems.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addQuoteItem = () => {
    setQuoteItems([...quoteItems, { id: Date.now(), item: '', units: 1, amount: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { Auth } = await import('aws-amplify');
      const user = await Auth.currentAuthenticatedUser();

      const profileResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/${user.username}`);
      const profile = await profileResponse.json();
      const vendorId = profile.currentVendorId;

      if (!vendorId) {
        alert('No vendor ID found. Please contact support.');
        setLoading(false);
        return;
      }

      let contractUrl = '';
      if (formData.contractFile) {
        const enhancedApiClient = (await import('../../api/enhancedApiClient')).default;
        const uploadResult = await enhancedApiClient.uploadFile(formData.contractFile);
        contractUrl = uploadResult.fileUrl;
      }

      const eventData = {
        id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: formData.eventName,
        eventType: formData.eventType,
        eventDate: formData.eventDate,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        billingModel,
        depositAmount: formData.depositAmount,
        depositDue: formData.depositDue,
        contractUrl,
        quoteItems: billingModel === 'Quote' ? quoteItems : [],
        message: formData.message
      };

      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        // Auto-create default checklist tasks for both vendor and client
        try {
          const enhancedApiClient = (await import('../../api/enhancedApiClient')).default;

          // 1. Check if vendor has custom templates — use those, otherwise use global defaults
          let vendorTasks = vendorChecklistDefaults;
          try {
            const templatesRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/templates`);
            if (templatesRes.ok) {
              const customTemplates = await templatesRes.json();
              if (customTemplates.length > 0) {
                vendorTasks = customTemplates;
              }
            }
          } catch (e) {
            console.log('No custom templates found, using defaults');
          }

          // 2. Create vendor checklist tasks
          for (const template of vendorTasks) {
            const dueDate = calculateDueDate(formData.eventDate, template.dueOffsetDays);
            await enhancedApiClient.createTask({
              eventId: eventData.id,
              name: template.name,
              details: template.details || '',
              dueDate,
              status: 'pending',
              taskType: 'vendor',
              vendorId: vendorId,
              createdByUserId: user.username,
              createdAt: new Date().toISOString()
            });
          }

          // 3. Create client planning checklist tasks
          for (const template of clientChecklistDefaults) {
            const dueDate = calculateDueDate(formData.eventDate, template.dueOffsetDays);
            await enhancedApiClient.createTask({
              eventId: eventData.id,
              name: template.name,
              details: template.details || '',
              dueDate,
              status: 'pending',
              taskType: 'client',
              createdByUserId: 'system',
              createdAt: new Date().toISOString()
            });
          }
        } catch (taskError) {
          console.error('Error creating default checklist tasks:', taskError);
        }

        setSuccess(true);
        setTimeout(() => { onClose(); navigate('/vendor/events', { state: { selectedEventId: eventData.id } }); }, 1500);
      } else {
        alert('Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: '#F9FAFB',
    outline: 'none'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  };

  const isDisabled = loading || !formData.eventName || !formData.eventDate || !formData.clientName || !formData.clientEmail || (billingModel === 'Contract' && !formData.contractFile) || (formData.depositAmount && !formData.depositDue);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '520px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif' }}>Create Event</h2>
        </div>

        {success ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', flex: 1 }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#6B9080', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <p style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', fontFamily: 'Georgia, serif' }}>Event Added Successfully!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {/* Scrollable Content — all fields vertically stacked */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Event Name *</label>
                <input type="text" required value={formData.eventName} onChange={(e) => setFormData({ ...formData, eventName: e.target.value })} style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Event Type</label>
                <select value={formData.eventType} onChange={(e) => setFormData({ ...formData, eventType: e.target.value })} style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}>
                  <option>Wedding</option>
                  <option>Corporate Event</option>
                  <option>Birthday Party</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Event Date *</label>
                <input type="date" required value={formData.eventDate} onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })} style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Client Name *</label>
                <input type="text" required value={formData.clientName} onChange={(e) => setFormData({ ...formData, clientName: e.target.value })} style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Client Email *</label>
                <input type="email" required value={formData.clientEmail} onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })} style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Deposit Amount <span style={{ color: '#9CA3AF' }}>(Optional)</span></label>
                <input type="number" value={formData.depositAmount} onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })} style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>

              {formData.depositAmount && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Deposit Due *</label>
                  <input type="date" required value={formData.depositDue} onChange={(e) => setFormData({ ...formData, depositDue: e.target.value })} style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                </div>
              )}

              {/* Billing Model */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Billing Model</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['Contract', 'Quote', 'None'].map(model => (
                    <button key={model} type="button" onClick={() => setBillingModel(model)} style={{
                      flex: 1, padding: '12px 16px',
                      backgroundColor: billingModel === model ? '#6B9080' : '#F9FAFB',
                      color: billingModel === model ? 'white' : '#6B7280',
                      border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                    }}>{model}</button>
                  ))}
                </div>
              </div>

              {/* Contract Upload */}
              {billingModel === 'Contract' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Contract *</label>
                  <input type="file" required accept=".pdf,.doc,.docx" onChange={(e) => setFormData({ ...formData, contractFile: e.target.files[0] })} style={{ display: 'none' }} id="contract-upload" />
                  <label htmlFor="contract-upload" style={{
                    padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#F9FAFB',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer'
                  }}>
                    <span style={{ color: formData.contractFile ? '#1F2937' : '#6B7280' }}>
                      {formData.contractFile ? formData.contractFile.name : 'Upload contract...'}
                    </span>
                    <span style={{ color: '#6B9080', fontSize: '20px' }}>+</span>
                  </label>
                </div>
              )}

              {/* Quote Items */}
              {billingModel === 'Quote' && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Items</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Units</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Amount</div>
                  </div>
                  {quoteItems.map(item => (
                    <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <input type="text" value={item.item} onChange={(e) => updateQuoteItem(item.id, 'item', e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px', backgroundColor: '#F9FAFB', outline: 'none' }}
                        onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                      <input type="number" value={item.units} onChange={(e) => updateQuoteItem(item.id, 'units', e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px', backgroundColor: '#F9FAFB', outline: 'none' }}
                        onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                      <input type="text" value={item.amount} onChange={(e) => updateQuoteItem(item.id, 'amount', e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px', backgroundColor: '#F9FAFB', outline: 'none' }}
                        onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                    </div>
                  ))}
                  <button type="button" onClick={addQuoteItem} style={{
                    padding: '8px 16px', backgroundColor: '#6B9080', color: 'white', border: 'none',
                    borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer'
                  }}>Add item</button>
                </div>
              )}

              {/* Message */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Message</label>
                <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Aa" rows={4}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '20px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '12px', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button type="button" onClick={onClose} style={{
                padding: '12px 24px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB',
                borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
              }}>Cancel</button>
              <button type="submit" disabled={isDisabled} style={{
                padding: '12px 24px',
                backgroundColor: isDisabled ? '#9CA3AF' : '#6B9080',
                color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }}>{loading ? 'Creating...' : 'Create Event'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateEventModal;
